import {inject, Injectable} from '@angular/core';
import {forkJoin, Observable, of} from 'rxjs';
import {catchError, map, switchMap} from 'rxjs/operators';
import {HttpClient} from '@angular/common/http';
import {BicToastService} from '@buildinginformationcloud/bic-ui-components';
import {CheckRule} from '../api/model/checkRule';

import {CheckRuleControllerService} from '../api/api/checkRuleController.service';
import {CheckRuleSet} from '../api/model/checkRuleSet';
import {WorkflowDefinitionControllerService} from '../api/api/workflowDefinitionController.service';
import {WorkflowDefinition} from '../api/model/workflowDefinition';
import {CheckMode, HybridRule, Ruleset} from '../components/ruleManagement/models/rule-management.models';
import {ProjectShareTarget, SharedProject, WorkflowService} from './workflow.service';
import {StateConfigService} from './state/state-config.service';
import {getIdsValidationIssues} from '../components/checks/bimCheck/components/ids/models/ids.model';
import {IdsAdapterService} from '../components/checks/bimCheck/components/ids/services/ids-adapter.service';
import {parsePruefpromptStepTitles} from '../models/pruefprompt-steps';
import {IdsThresholds, parseIdsThresholds} from '../models/report/ids-thresholds';
import {
  buildManifestFromState,
  createDirectRunState,
  createStateForRulesetSelection,
  parseRulesetAiConfig,
  RulesetAiConfig,
} from '../components/my-checks/my-checks-builder/my-checks-builder.model';
import {buildRuleIdMap,} from '../components/my-checks/shared/duplicate-check-prompt/check-duplication.model';

/**
 * Ein myCheck, der ein zu löschendes Prüfregelset bzw. eine zu löschende Prüfregel verwendet.
 * Spiegelt `MyCheckReferenceService.MyCheckUsage` im Backend.
 */
export interface MyCheckUsage {
  definitionId: string;
  name: string;
  projectId?: number;
  status?: string;
  /** Der Check verliert seine letzte Regelauswahl und wird dadurch zum Entwurf. */
  becomesDraft: boolean;
  /** false bei schreibgeschützten (BIC-bereitgestellten) Checks – die werden nicht angepasst. */
  editable: boolean;
}

/** Dateiendung des verschlüsselten Austauschformats für Prüfregelsets. */
export const RULESET_PACKAGE_EXTENSION = 'bicrs';

export interface RulesetDuplicationResult {
  sourceId: string;
  /** Fehlt, wenn die Kopie nicht verlinkt werden konnte – der Bezug bleibt dann stehen. */
  newId?: string;
  /** Alte→neue Prüfregel-ID, soweit die Quelle die Regeln mitliefert (KI-Sets). */
  ruleIdMap: Record<string, string>;
}

/**
 * Ergebnis eines Regelset-Imports. Spiegelt `RulesetImportResult` im Backend.
 *
 * Ein Import gelingt auch dann, wenn nicht jeder Verweis auflösbar war – was fehlt, steht in
 * `warnings` und muss dem Nutzer angezeigt werden.
 */
export interface RulesetImportResult {
  rulesetId: string;
  type: string;
  name: string;
  ruleCount: number;
  /** Projekt, aus dem das Paket exportiert wurde (rein informativ). */
  sourceProjectName?: string;
  warnings?: string[];
  documentClassesCreated?: string[];
  documentClassesUnresolved?: string[];
}

/**
 * Stand des myCheck-Vorlagenlimits des Mandanten. Spiegelt `MyCheckQuotaDto` im Backend.
 *
 * `limit`/`remaining` sind `null`, wenn das Abo keine Grenze setzt (Enterprise) – dann darf ohne
 * Vorabprüfung angelegt werden.
 */
export interface MyCheckQuota {
  limit: number | null;
  used: number;
  remaining: number | null;
}

/**
 * Leitet den Quota-Stand aus der tatsächlichen Anzahl eigener myChecks (Regelsets) ab.
 *
 * <p>So bleibt die Frontend-Warnung stabil, selbst wenn der Backend-Zähler zusätzlich Prüfläufe
 * einbezieht: für die Anzeige und Sperrlogik zählt nur die Anzahl eigener myChecks.
 */
export function deriveQuotaFromMyCheckCount(quota: MyCheckQuota, myCheckCount: number): MyCheckQuota {
  const used = Math.max(0, myCheckCount);
  if (quota.limit == null) {
    return {...quota, used, remaining: null};
  }
  return {
    ...quota,
    used,
    remaining: Math.max(quota.limit - used, 0),
  };
}

/**
 * Effektive Dateityp-Einschränkung eines Regelsets inklusive regelbezogener Einschränkungen.
 *
 * Regelwerte gewinnen gegenüber der Set-Defaultliste: sobald mindestens eine Regel Dateitypen
 * deklariert, werden nur diese angeboten (geschnitten mit der Setliste, falls möglich).
 */
export function mergeRulesetAndRuleAllowedFileTypes(
  setAllowedFileTypes: string[],
  ruleAllowedFileTypeGroups: string[][],
): string[] {
  const setAllowed = normalizeAllowedValues(setAllowedFileTypes);
  const ruleAllowed = normalizeAllowedValues(ruleAllowedFileTypeGroups.flat());
  if (ruleAllowed.length === 0) return setAllowed;
  const intersection = setAllowed.filter(type => ruleAllowed.includes(type));
  return intersection.length > 0 ? intersection : ruleAllowed;
}

/**
 * Effektive Dokumentenklassen-Einschränkung eines Regelsets inklusive regelbezogener Einschränkungen.
 *
 * Leere Set-Auswahl bedeutet weiterhin "alle". Existieren regelbezogene Klassenlisten, schränken
 * diese die Auswahl ein.
 */
export function mergeRulesetAndRuleAllowedDocumentClasses(
  setAllowedDocumentClasses: string[],
  ruleAllowedDocumentClassGroups: string[][],
): string[] {
  const setAllowed = normalizeAllowedValues(setAllowedDocumentClasses);
  const ruleAllowed = normalizeAllowedValues(ruleAllowedDocumentClassGroups.flat());
  if (ruleAllowed.length === 0) return setAllowed;
  if (setAllowed.length === 0) return ruleAllowed;
  const intersection = setAllowed.filter(documentClassId => ruleAllowed.includes(documentClassId));
  return intersection.length > 0 ? intersection : ruleAllowed;
}

function normalizeAllowedValues(values: string[]): string[] {
  return [...new Set(values
    .filter((value): value is string => typeof value === 'string')
    .map(value => value.trim())
    .filter(Boolean))];
}

/**
 * Die Vorabprüfung des Vorlagenlimits hat abgelehnt – es wurde noch nichts angelegt.
 *
 * <p>Trägt bewusst `status = 402` wie die Antwort des Backends: die Aufrufer unterscheiden ihre
 * Fehlermeldungen schon an diesem Status und brauchen keinen zweiten Sonderfall.
 */
export class MyCheckTemplateQuotaError extends Error {
  readonly status = 402;

  constructor(readonly quota: MyCheckQuota, readonly needed: number) {
    super(`Für diese Übernahme werden ${needed} myCheck-Vorlagen gebraucht, frei sind noch`
      + ` ${quota.remaining ?? 0}. Löschen Sie eine bestehende Vorlage oder erweitern Sie Ihr Abo.`);
  }
}

/** Eine einzelne Regel innerhalb eines Regelsets (typunabhängig normalisiert) */
export interface RulesetRule {
  id: string;
  name: string;
  description?: string;
  /** Kurzkürzel der Regel (z.B. "A1.8"); leer = kein Kürzel vergeben. */
  code?: string;
}

/**
 * Service für die Prüfregelverwaltung.
 * Aggregiert Daten aus der generierten CheckRuleController-API (KI-Rulesets),
 * Workflow-Definitions (Hybrid-Rulesets) und Workflow-CheckRules (IDS) zu einem einheitlichen Ruleset-Modell.
 */
@Injectable({providedIn: 'root'})
export class RulesetService {
  private checkRuleApi = inject(CheckRuleControllerService);
  private definitionApi = inject(WorkflowDefinitionControllerService);
  private flowService = inject(WorkflowService);
  private stateConfig = inject(StateConfigService);
  private http = inject(HttpClient);
  private idsAdapter = inject(IdsAdapterService);
  private toastService = inject(BicToastService);

  /** Gerade gemeldete Ladefehler – mehrere gleichzeitige Aufrufer sollen nur einen Toast sehen. */
  private readonly recentLoadFailures = new Set<string>();

  private get basePath(): string {
    return (this.definitionApi as any).basePath ?? '';
  }

  /**
   * Wie {@link duplicateRuleset}, plus die Zuordnung alte→neue ID – gebraucht, um die Regelauswahl
   * eines Duplikats auf die Kopien umzubiegen.
   *
   * <p>KI- und HYBRID-Sets brauchen den Ladeschritt: ihre Regeln haben eigene IDs (KI-Regeln bzw.
   * die Kind-Definitionen `HYBRID_RULE`, die das Backend beim Duplizieren mitkopiert). IDS-
   * Spezifikationen tragen die Set-ID als Präfix.
   *
   * <p>Scheitert das Lesen des Originals, scheitert der ganze Aufruf – ohne die Regeln wäre die
   * Zuordnung leer, und eine Teilauswahl zeigte im Duplikat weiter auf die Regeln des Originals.
   */
  duplicateRulesetWithMapping(
    ruleset: Ruleset,
    projectId: number,
    duplicatedName: string,
  ): Observable<RulesetDuplicationResult> {
    if (ruleset.type === 'KI') {
      return this.getKiRuleset(ruleset.id, projectId).pipe(
        switchMap(original => this.duplicateRuleset(ruleset, projectId, duplicatedName).pipe(
          map(copy => ({
            sourceId: ruleset.id,
            newId: copy?.id,
            ruleIdMap: buildRuleIdMap(original?.rules ?? [], copy?.rules ?? []),
          })),
        )),
      );
    }

    if (ruleset.type === 'HYBRID') {
      // Bewusst nicht über getHybridRules: das tarnt einen Ladefehler als leere Regelliste.
      return this.flowService.getDefinition(ruleset.id, projectId).pipe(
        switchMap(original => this.duplicateRuleset(ruleset, projectId, duplicatedName).pipe(
          map((copy: WorkflowDefinition) => ({
            sourceId: ruleset.id,
            newId: copy?.id,
            ruleIdMap: buildRuleIdMap(this.hybridRuleRefs(original), this.hybridRuleRefs(copy)),
          })),
        )),
      );
    }

    return this.duplicateRuleset(ruleset, projectId, duplicatedName).pipe(
      map(copy => ({sourceId: ruleset.id, newId: copy?.id, ruleIdMap: {}})),
    );
  }

  /** Duplicate a ruleset and persist the copy in the current project scope */
  duplicateRuleset(ruleset: Ruleset, projectId: number, duplicatedName: string): Observable<any> {
    if (ruleset.type === 'KI') {
      return this.getKiRuleset(ruleset.id, projectId).pipe(
        switchMap(original => {
          const copiedRules = (original.rules ?? []).map(rule => ({
            ...rule,
            id: undefined,
            createdAt: undefined,
            updatedAt: undefined,
          }));
          const duplicate: CheckRuleSet = {
            ...original,
            id: undefined,
            createdAt: undefined,
            updatedAt: undefined,
            name: duplicatedName,
            prebuilt: false,
            projectId,
            rules: copiedRules,
          };
          return this.createKiRuleset(duplicate);
        }),
      );
    }

    if (ruleset.type === 'HYBRID') {
      // Serverseitig: das Backend kopiert die Kindregeln (HYBRID_RULE) mit und biegt das Manifest
      // der Kopie auf sie um. Die frühere Manifest-Kopie im Client zeigte weiter auf die Kindregeln
      // des Originals – Bearbeiten der Kopie hätte das Original verändert.
      return this.http.post<WorkflowDefinition>(
        `${this.basePath}/workflows/definitions/${ruleset.id}/duplicate-hybrid`,
        {name: duplicatedName},
        {params: {projectId: String(projectId)}},
      );
    }

    // IDS: fetch the original rule to copy its XML content
    return this.http.get<any>(`${this.basePath}/workflows/rules/${ruleset.id}`, {
      params: {projectId: String(projectId)},
    }).pipe(
      switchMap(original => {
        // Update XML metadata: title, author, date
        const updatedXmlData = this.updateIdsXmlMetadata(
          original.ruleData,
          duplicatedName,
          new Date().toISOString().split('T')[0] // Today's date in YYYY-MM-DD format
        );
        const duplicate = {
          name: duplicatedName,
          description: original.description ?? '', // Bug Fix #1: Copy description
          category: 'IDS',
          projectId,
          ruleData: updatedXmlData,
          ruleDataFormat: original.ruleDataFormat ?? 'application/xml',
          active: true,
        };
        return this.http.post<any>(`${this.basePath}/workflows/rules`, duplicate, {
          params: {projectId: String(projectId)},
        });
      }),
    );
  }

  /**
   * Wie viele myCheck-Vorlagen der Mandant noch anlegen darf.
   *
   * <p>Für die Vorabprüfung, damit der Nutzer nicht erst in einen 402 läuft. Zwingend bei der
   * Share-Übernahme, die mehrere Regelsets nacheinander kopiert: ein Abbruch mitten in der Schleife
   * hinterließe eine halbe Übernahme.
   *
   * <p>Bewusst über `HttpClient` und nicht über den generierten Client: der kennt den Endpunkt erst
   * nach dem nächsten `api-client-generation.sh` – wie die übrigen `workflows/...`-Aufrufe hier.
   */
  getQuota(projectId: number): Observable<MyCheckQuota> {
    return this.http.get<MyCheckQuota>(`${this.basePath}/workflows/rulesets/quota`, {
      params: {projectId: String(projectId)},
    }).pipe(
      switchMap(quota => this.listRulesets(projectId).pipe(
        // Aus anderen Projekten geteilte Sets gehören ihrem Eigentümerprojekt und zählen dort.
        map(({custom}) => deriveQuotaFromMyCheckCount(quota, custom.filter(r => !r.linkedIn).length)),
        catchError(() => of(quota)),
      )),
    );
  }

  // ─── Direktstart eines Prüfregelsets ─────────────────────────────────────────

  /**
   * Macht ein Prüfregelset direkt ausführbar und liefert die `definitionId`, mit der der bestehende
   * Ausführungspfad (`.../my-checks/execute?definitionId=…`) aufgerufen wird.
   *
   * <p>Baut aus den **Default-Prüflaufeinstellungen** plus diesem einen Set genau das Manifest, das
   * ein Nutzer bekäme, der im Builder nur den Namen einträgt, das Set wählt und überall die Defaults
   * bestätigt – und legt damit den automatisch erzeugten, für ihn unsichtbaren myCheck an bzw.
   * aktualisiert ihn. Der Ausführungsprozess selbst bleibt dadurch unverändert.
   *
   * <p>Das Manifest wird bei JEDEM Aufruf neu gebaut: bei KI-Sets stecken Prompts, Parametermodell und
   * die daraus abgeleiteten Berichts-Detailspalten im Manifest. Würde die Definition nur einmal
   * erzeugt, zeigte der Bericht nach einer Set-Änderung Spalten, die es nicht mehr gibt.
   *
   * <p>Nur KI-Sets brauchen den zusätzlichen Ladeschritt (`configJson`); IDS und Hybrid lösen ihre
   * Regeln zur Laufzeit aus dem Set auf.
   */
  prepareDirectRun(ruleset: Ruleset, projectId: number): Observable<string> {
    const ai$: Observable<RulesetAiConfig | undefined> = ruleset.type === 'KI'
      ? this.getKiRuleset(ruleset.id, projectId).pipe(
        map(set => parseRulesetAiConfig(set?.configJson)),
        // Ohne die Set-Konfiguration löst das Backend die Prompts zur Laufzeit aus dem Set auf –
        // der Lauf funktioniert, nur die Berichts-Detailspalten fallen auf den Default zurück.
        catchError(() => of(undefined)),
      )
      : of(undefined);

    return ai$.pipe(
      switchMap(ai => {
        const state = createDirectRunState({
          id: ruleset.id,
          name: ruleset.name,
          description: ruleset.description,
          type: ruleset.type,
          pruefebene: ruleset.pruefebene,
          checkMode: ruleset.checkMode,
          allowedFileTypes: ruleset.allowedFileTypes,
          allowedDocumentClasses: ruleset.allowedDocumentClasses,
        }, ai);
        const manifest = buildManifestFromState(state);
        return this.flowService.upsertAutoDefinitionForRuleset(ruleset.id, manifest, projectId);
      }),
      map(definition => {
        if (!definition?.id) {
          throw new Error('Der Direktstart konnte nicht vorbereitet werden: keine Definition erhalten.');
        }
        return definition.id;
      }),
    );
  }

  /**
   * Legt aus einem myCheck einen normalen Prüflauf an – nicht den unsichtbaren Auto-Prüflauf des
   * Direktstarts ({@link prepareDirectRun}). Vorbelegt ist allein die Regelauswahl.
   *
   * <p>Bei KI-Sets werden die Prompts des Sets geladen, weil der Builder das beim manuellen
   * Auswählen ebenso tut; ohne sie löst das Backend sie zur Laufzeit auf.
   */
  createCheckRunForRuleset(ruleset: Ruleset, projectId: number, name: string): Observable<WorkflowDefinition> {
    const ai$: Observable<RulesetAiConfig | undefined> = ruleset.type === 'KI'
      ? this.getKiRuleset(ruleset.id, projectId).pipe(
        map(set => parseRulesetAiConfig(set?.configJson)),
        catchError(() => of(undefined)),
      )
      : of(undefined);

    return ai$.pipe(
      switchMap(ai => {
        const state = createStateForRulesetSelection(ruleset, name, ai);
        return this.flowService.saveDefinition(buildManifestFromState(state), projectId);
      }),
    );
  }

  // ─── Verwendung in myChecks ──────────────────────────────────────────────────

  /**
   * myChecks, die die angegebenen Prüfregelsets/Prüfregeln verwenden – für die Löschbestätigung.
   * Das Backend erweitert Set-IDs selbst um die IDs ihrer Einzelregeln bzw. IDS-Spezifikationen,
   * hier genügt also die ID des zu löschenden Objekts.
   */
  findMyCheckUsages(ids: string[], projectId?: number): Observable<MyCheckUsage[]> {
    const filtered = (ids ?? []).filter(id => !!id);
    if (filtered.length === 0) return of([]);
    // projectId ist serverseitig Pflicht (Privilegien-Prüfung), daher Fallback auf das aktive Projekt.
    const resolvedProjectId = projectId ?? this.stateConfig.getProjectId();
    const params: any = {ids: filtered};
    if (resolvedProjectId != null) params.projectId = String(resolvedProjectId);
    return this.http.get<MyCheckUsage[]>(`${this.basePath}/workflows/definitions/usages`, {params}).pipe(
      catchError(() => of([])),
    );
  }

  // ─── List all rulesets ───────────────────────────────────────────────────────

  /**
   * Load all rulesets for a project, split into prebuilt and custom.
   * Combines CheckRuleSets (→ KI type), IDS rules (→ workflows/rules),
   * and workflow definitions (→ Hybrid).
   */
  listRulesets(projectId?: number): Observable<{ prebuilt: Ruleset[]; custom: Ruleset[] }> {
    const ki$ = this.loadKiRulesets(projectId);
    const hybrid$ = this.loadHybridRulesets(projectId);
    const ids$ = this.loadIdsRules(projectId);

    return forkJoin([ki$, hybrid$, ids$]).pipe(
      map(([kiRulesets, hybridRulesets, idsRulesets]) => {
        const all = [...kiRulesets, ...hybridRulesets, ...idsRulesets];
        return {
          prebuilt: all.filter(r => r.isPrebuilt),
          custom: all.filter(r => !r.isPrebuilt),
        };
      }),
    );
  }

  // ─── KI Rulesets (via generated CheckRuleController API) ─────────────────

  /** Create a new IDS rule via workflows/rules */
  createIdsRule(name: string, projectId: number): Observable<any> {
    const rule = {
      name,
      category: 'IDS',
      projectId,
      ruleData: null,
      ruleDataFormat: 'application/xml',
      active: true,
    };
    return this.http.post<any>(`${this.basePath}/workflows/rules`, rule, {
      params: {projectId: String(projectId)},
    });
  }

  /** Delete an IDS rule via workflows/rules */
  deleteIdsRule(id: string, projectId: number, removeShares = false): Observable<any> {
    return this.http.delete(`${this.basePath}/workflows/rules/${id}`, {
      params: {projectId: String(projectId), removeShares: String(removeShares)},
    });
  }

  // ─── IDS Rules (via workflows/rules API) ───────────────────────────────────

  /**
   * Delete a ruleset (KI, IDS, or Hybrid).
   *
   * @param removeShares „aus allen Projekten löschen": das Backend hebt vorher alle Freigaben an
   *                     andere Projekte auf – nur mit der Teilen-Regel für jedes. Ohne das Flag ist
   *                     Löschen gesperrt, solange das Set geteilt ist.
   */
  deleteRuleset(ruleset: Ruleset, projectId?: number, removeShares = false): Observable<any> {
    if (ruleset.type === 'HYBRID') {
      if (!removeShares) {
        return this.deleteHybridRuleset(ruleset.id);
      }
      return this.http.delete(`${this.basePath}/workflows/definitions/${ruleset.id}`, {
        params: {projectId: String(projectId ?? this.stateConfig.getProjectId()), removeShares: 'true'},
      });
    }
    if (ruleset.type === 'IDS') {
      return this.deleteIdsRule(ruleset.id, projectId ?? 0, removeShares);
    }
    return this.deleteKiRuleset(ruleset.id, projectId, removeShares);
  }

  /**
   * Lädt die einzelnen Regeln eines Regelsets, normalisiert auf {@link RulesetRule}.
   * Unterstützt KI-Sets (CheckRuleSet.rules), Hybrid-Sets (Workflow-Steps) und
   * IDS-Regeln (die selbst eine einzelne Regel darstellen).
   */
  getRulesetRules(ruleset: Ruleset, projectId?: number): Observable<RulesetRule[]> {
    const failed = (err: unknown): Observable<RulesetRule[]> => {
      this.reportLoadFailure(ruleset.name
        ? `Die Prüfregeln von „${ruleset.name}" konnten nicht geladen werden.`
        : 'Die Prüfregeln eines myChecks konnten nicht geladen werden.', err);
      return of([]);
    };
    if (ruleset.type === 'KI') {
      return this.getKiRuleset(ruleset.id, projectId, projectId == null).pipe(
        map(set => (set.rules ?? []).map(r => {
          // Das Kürzel liegt bei KI-Regeln im configJson (analog zum KI-Editor), nicht als eigenes Feld.
          let ruleConfig: any = {};
          try {
            ruleConfig = (r as any).configJson ? JSON.parse((r as any).configJson) : {};
          } catch {
            ruleConfig = {};
          }
          return {
            id: r.id ?? '',
            name: r.name ?? 'Regel',
            description: r.description ?? '',
            code: ruleConfig.ruleShortCode ?? ruleConfig.shortCode ?? undefined,
          };
        })),
        catchError(failed),
      );
    }
    if (ruleset.type === 'HYBRID') {
      return this.getHybridRules(ruleset.id).pipe(
        map(rules => rules.map(r => ({id: r.id, name: r.name, description: r.description, code: r.shortCode || undefined}))),
        catchError(failed),
      );
    }
    // IDS: Spezifikationen als Regeln laden
    const params: any = {};
    if (projectId != null) params.projectId = String(projectId);
    return this.http.get<any>(`${this.basePath}/workflows/rules/${ruleset.id}`, {params}).pipe(
      map(rule => this.parseIdsSpecsAsRules(ruleset.id, rule.ruleData)),
      catchError(failed),
    );
  }

  /** Alle globalen Regelwerke aller drei Typen inkl. Entwürfe (Admin-Übersicht). */
  listGlobalRulesets(): Observable<Ruleset[]> {
    // Je Art sichtbar melden und leer weitermachen – wie loadKiRulesets & Co.: eine Art, die nicht
    // lädt, soll die beiden anderen nicht mit leer machen.
    const failed = (message: string) => (err: unknown): Observable<Ruleset[]> => {
      this.reportLoadFailure(message, err);
      return of([]);
    };
    const ids$ = this.http.get<any[]>(this.idsGlobalBase).pipe(
      map(rules => rules.map(r => this.mapGlobalIdsToRuleset(r))),
      catchError(failed('Zentral bereitgestellte IDS-myChecks konnten nicht geladen werden.')));
    const ki$ = this.http.get<any[]>(this.kiGlobalBase).pipe(
      // status/readOnly aus der Entity überschreiben (der Standard-Mapper leitet status aus der Config ab).
      map(sets => sets.map(s => ({
        ...this.mapCheckRuleSetToRuleset(s),
        status: (s.status as Ruleset['status']) ?? 'PUBLISHED',
        readOnly: true,
      }))),
      catchError(failed('Zentral bereitgestellte KI-myChecks konnten nicht geladen werden.')));
    const hybrid$ = this.http.get<any[]>(this.hybridGlobalBase).pipe(
      map(defs => defs.map(d => ({...this.mapDefinitionToRuleset(d), readOnly: true}))),
      catchError(failed('Zentral bereitgestellte Hybrid-myChecks konnten nicht geladen werden.')));
    return forkJoin([ids$, ki$, hybrid$]).pipe(map(([a, b, c]) => [...a, ...b, ...c]));
  }

  /**
   * Stabile, kurze Prüfschritt-Titel eines KI-Regelsets, geordnet je Prüfschritt.
   *
   * Quelle ist der gespeicherte Prüfprompt selbst: er nummeriert und benennt die Schritte
   * („1. Beleg-Relevanz", …). Das ist deterministisch, kostet keinen KI-Aufruf und steht sofort
   * nach dem Speichern zur Verfügung. Erst wenn ein Schritt dort keine Überschrift trägt (reiner
   * Fließtext), greift die beim Speichern erzeugte KI-Interpretation für DIESEN Schritt.
   *
   * Der Bericht nutzt die Titel ausschließlich als Lückenfüller für Prüfschritte, denen das
   * Prüfmodell keine eigene Überschrift mitgegeben hat.
   */
  getKiStepTitles(id: string): Observable<string[]> {
    return this.getKiRuleset(id).pipe(
      map(set => {
        try {
          const config = set?.configJson ? JSON.parse(set.configJson) : {};
          const fromPrompt = parsePruefpromptStepTitles(config?.pruefprompt);
          const interpreted: string[] = Array.isArray(config?.aiStepInterpretation)
            ? config.aiStepInterpretation.map((t: unknown) => String(t ?? '').trim())
            : [];
          const length = Math.max(fromPrompt.length, interpreted.length);
          return Array.from({length}, (_, i) => fromPrompt[i] || interpreted[i] || '');
        } catch {
          return [];
        }
      }),
      // Die Titel sind nur Lückenfüller im Bericht – der Bericht bleibt ohne sie brauchbar, der
      // Fehler soll aber nicht unsichtbar sein.
      catchError(err => {
        this.reportStepTitleFailure(id, err);
        return of([] as string[]);
      }),
    );
  }

  // ─── Export / Import (.bicrs) ────────────────────────────────────────────────

  /**
   * Lädt ein Prüfregelset als verschlüsseltes Austauschpaket herunter.
   *
   * Die Verschlüsselung findet ausschließlich serverseitig statt – der Schlüssel liegt als
   * Umgebungsvariable in der Instanz. Das Frontend bewegt nur einen undurchsichtigen Blob.
   *
   * Der Dateiname kommt aus dem `Content-Disposition`-Header des Servers (dort wird er aus dem
   * Regelset-Namen gebildet und bereinigt), mit dem Regelset-Namen als Rückfallebene: bei
   * Cross-Origin-Antworten ist der Header nur lesbar, wenn er über
   * `Access-Control-Expose-Headers` freigegeben ist.
   */
  exportRuleset(rulesetId: string, projectId: number): Observable<{ blob: Blob; fileName: string }> {
    return this.http.get(`${this.basePath}/workflows/rulesets/${rulesetId}/export`, {
      params: {projectId: String(projectId)},
      responseType: 'blob',
      observe: 'response',
    }).pipe(
      map(response => ({
        blob: response.body as Blob,
        fileName: this.fileNameFromResponse(response.headers.get('content-disposition')),
      })),
    );
  }

  /**
   * Importiert ein Austauschpaket in ein Projekt.
   *
   * @param createMissingDocumentClasses Legt im Zielprojekt fehlende Dokumentenklassen an.
   *   Standard aus: der Import soll die Projektkonfiguration nicht unaufgefordert erweitern.
   */
  importRuleset(file: File, projectId: number,
                options?: { name?: string; createMissingDocumentClasses?: boolean },
  ): Observable<RulesetImportResult> {
    const formData = new FormData();
    formData.append('file', file, file.name);

    const params: Record<string, string> = {projectId: String(projectId)};
    if (options?.name) params['name'] = options.name;
    if (options?.createMissingDocumentClasses) params['createMissingDocumentClasses'] = 'true';

    return this.http.post<RulesetImportResult>(
      `${this.basePath}/workflows/rulesets/import`, formData, {params});
  }

  /** Löst den Download eines Pakets im Browser aus. */
  triggerDownload(blob: Blob, fileName: string): void {
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = fileName;
    anchor.click();
    URL.revokeObjectURL(url);
  }

  /**
   * Liefert die beim Speichern des KI-Regelsets interpretierten, stabilen Prüfschritt-Titel
   * (`configJson.aiStepInterpretation`, geordnet je Prüfschritt). Best-effort: leeres Array,
   * wenn nicht vorhanden/parsebar.
   */
  getKiStepInterpretation(id: string): Observable<string[]> {
    return this.getKiRuleset(id).pipe(
      map(set => {
        try {
          const config = set?.configJson ? JSON.parse(set.configJson) : {};
          const titles = config?.aiStepInterpretation;
          return Array.isArray(titles) ? titles.map((t: unknown) => String(t ?? '').trim()) : [];
        } catch {
          return [];
        }
      }),
      catchError(err => {
        this.reportStepTitleFailure(id, err);
        return of([] as string[]);
      }),
    );
  }

  /**
   * Bug Fix #2 & #3: Updates IDS XML metadata for duplicated rules.
   * Sets: title (from name), date (today).
   */
  private updateIdsXmlMetadata(xmlString: string | null | undefined, newTitle: string, today: string): string {
    if (!xmlString) return xmlString ?? '';

    try {
      const parser = new DOMParser();
      const doc = parser.parseFromString(xmlString, 'application/xml');

      // Check for XML parse errors
      if (doc.documentElement.tagName === 'parsererror') {
        return xmlString;
      }

      // Find or create <ids:info> element
      const nsUri = 'http://standards.buildingsmart.org/IDS';
      let infoEl = this.getElementByLocalName(doc.documentElement, 'info');

      if (!infoEl) {
        // Create new <ids:info> element if it doesn't exist
        infoEl = doc.createElementNS(nsUri, 'ids:info');
        const firstChild = doc.documentElement.firstChild;
        if (firstChild) {
          doc.documentElement.insertBefore(infoEl, firstChild);
        } else {
          doc.documentElement.appendChild(infoEl);
        }
      }

      // Update or create <title>
      let titleEl = this.getElementByLocalName(infoEl, 'title');
      if (!titleEl) {
        titleEl = doc.createElementNS(nsUri, 'ids:title');
        infoEl.appendChild(titleEl);
      }
      titleEl.textContent = newTitle;

      // Update or create <date>
      let dateEl = this.getElementByLocalName(infoEl, 'date');
      if (!dateEl) {
        dateEl = doc.createElementNS(nsUri, 'ids:date');
        infoEl.appendChild(dateEl);
      }
      dateEl.textContent = today;

      // Serialize back to XML string
      const serializer = new XMLSerializer();
      return serializer.serializeToString(doc);
    } catch (error) {
      console.error('Error updating IDS XML metadata:', error);
      return xmlString ?? '';
    }
  }

  /** Helper: Find element by local name (ignores namespace prefix) */
  private getElementByLocalName(parent: Element, localName: string): Element | null {
    for (let i = 0; i < parent.childNodes.length; i++) {
      const child = parent.childNodes[i];
      if (child.nodeType === 1) {
        const el = child as Element;
        if (el.localName === localName || el.tagName.endsWith(':' + localName)) {
          return el;
        }
      }
    }
    return null;
  }
  /** Create a new KI ruleset – im Projekt des Sets bzw. im aktuellen Projekt. */
  createKiRuleset(ruleSet: CheckRuleSet): Observable<CheckRuleSet> {
    return this.http.post<CheckRuleSet>(`${this.basePath}/check-rules`, ruleSet, {
      params: {projectId: String(ruleSet.projectId ?? this.stateConfig.getProjectId())},
    });
  }

  private get idsGlobalBase(): string {
    return `${this.basePath}/workflows/rules/global`;
  }

  private get kiGlobalBase(): string {
    return `${this.basePath}/check-rules/global`;
  }

  // ─── Global (zentral bereitgestellte) Regelwerke – Admin-Bereich ────────────
  // Drei Backend-Quellen: IDS → /workflows/rules/global, KI → /check-rules/global,
  // HYBRID → /workflows/definitions/global. Nur mit GLOBAL_CHECKRULE_ADMIN erreichbar.

  private get hybridGlobalBase(): string {
    return `${this.basePath}/workflows/definitions/global`;
  }

  /**
   * Meldet einen Ladefehler sichtbar, statt ihn als leere Liste zu tarnen. Die Aufrufer bekommen
   * danach trotzdem eine leere Liste: in den Übersichten werden die drei Arten kombiniert, und eine
   * Art, die nicht lädt, soll die anderen nicht mit leer machen.
   */
  /**
   * 404 ist hier ein erwarteter Zustand, kein Fehler: ältere Prüfläufe zeigen teils noch auf KI-Sets
   * eines anderen Projekts, die nicht (mehr) hierher geteilt sind (Übergabe Abschnitt 10). Das
   * gehört ins Log, nicht als Fehler-Toast auf jede Berichtsseite. Alles andere wird gemeldet.
   */
  private reportStepTitleFailure(rulesetId: string, err: unknown): void {
    if ((err as { status?: number } | null)?.status === 404) {
      console.warn(`[RulesetService] KI-myCheck ${rulesetId} ist in diesem Projekt nicht verfügbar – Prüfschritt-Titel entfallen.`);
      return;
    }
    this.reportLoadFailure('Die Prüfschritt-Titel eines KI-myChecks konnten nicht geladen werden.', err);
  }

  /**
   * Nur die **veröffentlichten** globalen Regelwerke, in der Auswahl-Form des myCheck-Builders.
   *
   * <p>Gebraucht für den Admin-Modus des Builders: eine zentral bereitgestellte myCheck-Kopie darf
   * ausschließlich zentral bereitgestellte Regelwerke referenzieren. Ein projekt-eigenes Regelset
   * wäre bei den Empfängern nicht auflösbar.
   *
   * <p><b>Warum nicht `listRulesets(undefined)`:</b> das sieht zwar nach „nur global" aus, ist es
   * aber nicht. `GET check-rules` ohne `projectId` läuft im Backend in
   * `CheckRuleService.listByProject(null)` → `findAll()`, liefert also **die KI-Regelsets aller
   * Projekte**; der Client-Filter behält davon jedes projekt-eigene Set (`s.projectId != null`).
   * IDS und HYBRID wiederum kommen ohne `projectId` gar nicht durch den
   * `AllowedPrivilege`-Aspekt (403) und fehlen dann komplett. Das Ergebnis wäre also
   * gleichzeitig zu viel und zu wenig.
   *
   * <p><b>Warum nicht `listGlobalRulesets()`:</b> das enthält bewusst auch Entwürfe (Admin-Übersicht)
   * und lässt den Status auf `PUBLISHED` stehen. Der Builder filtert aber auf
   * `status === 'ACTIVE'` ({@code isRulesetValid}) – die Liste wäre leer. Hier werden Entwürfe
   * verworfen und der Status auf `ACTIVE` normalisiert.
   *
   * @return dieselbe Form wie {@link #listRulesets}; `custom` ist immer leer, weil es im
   *         Adminbereich keine projekt-eigenen Regelwerke gibt
   */
  listGlobalRulesetsForSelection(): Observable<{ prebuilt: Ruleset[]; custom: Ruleset[] }> {
    return this.listGlobalRulesets().pipe(
      map(rulesets => ({
        prebuilt: rulesets
          // Entwürfe und Archivierte sind bei Empfängern nicht auflösbar.
          .filter(r => r.status === 'PUBLISHED' || r.status === 'ACTIVE')
          .map(r => ({...r, status: 'ACTIVE' as Ruleset['status'], isPrebuilt: true, readOnly: true})),
        custom: [] as Ruleset[],
      })),
    );
  }

  createGlobalDraft(ruleset: Ruleset): Observable<{ id: string }> {
    return this.http.post<any>(`${this.globalBaseFor(ruleset.type)}/${ruleset.id}/draft`, {});
  }

  /** Globalen KI-Entwurf aktualisieren (vom KI-Editor im Admin-Modus). */
  updateGlobalKiRuleset(id: string, set: CheckRuleSet): Observable<CheckRuleSet> {
    return this.http.put<CheckRuleSet>(`${this.kiGlobalBase}/${id}`, set);
  }

  /** Globalen HYBRID-Entwurf aktualisieren (vom Hybrid-/Workflow-Editor im Admin-Modus). */
  updateGlobalHybridDefinition(id: string, manifest: any): Observable<WorkflowDefinition> {
    return this.http.put<WorkflowDefinition>(`${this.hybridGlobalBase}/${id}`, {
      manifest,
      definitionType: 'HYBRID_RULESET'
    });
  }

  /** Globale HYBRID-Definition (Set oder Kind-Regel) als Entwurf anlegen. */
  createGlobalHybridDefinition(manifest: any, definitionType: 'HYBRID_RULESET' | 'HYBRID_RULE' = 'HYBRID_RULESET'): Observable<WorkflowDefinition> {
    return this.http.post<WorkflowDefinition>(this.hybridGlobalBase, {manifest, definitionType});
  }

  /**
   * Ergänzt Eigentümer und Projektliste eines zwischen Projekten geteilten Sets. Ein Set aus einem
   * anderen Projekt ist hier nur lesbar – aber nicht „vorgefertigt": es gehört einem Projekt der
   * Organisation, nicht dem Adminbereich.
   *
   * <p>`sharedProjects` fehlt noch im generierten Modell (src/app/api ist gitignored), daher der
   * Zugriff über die rohe Antwort.
   */
  private withProjectSharing(ruleset: Ruleset, raw: unknown, currentProjectId?: number): Ruleset {
    const source = raw as {projectId?: number | null; sharedProjects?: SharedProject[] | null};
    const ownerProjectId = source.projectId ?? undefined;
    const linkedIn = ownerProjectId != null && currentProjectId != null && ownerProjectId !== currentProjectId;
    return {
      ...ruleset,
      projectId: ownerProjectId,
      sharedProjects: source.sharedProjects ?? undefined,
      linkedIn,
      readOnly: linkedIn ? true : ruleset.readOnly,
      isPrebuilt: linkedIn ? false : ruleset.isPrebuilt,
    };
  }

  // ─── Regelsets zwischen Projekten teilen (normale Nutzer) ──────────────────
  //
  // Handgeschrieben wie die Freigabe-Aufrufe im WorkflowService (src/app/api ist generiert und
  // gitignored). Eine Schnittstelle für alle drei Arten – das Backend löst die ID selbst auf.
  // `projectId` ist jeweils das Projekt, aus dem heraus gearbeitet wird.

  private projectSharesPath(rulesetId: string): string {
    return `${this.basePath}/workflows/rulesets/${rulesetId}/project-shares`;
  }

  /** Mögliche Zielprojekte; `projectId` ist das Eigentümerprojekt. */
  listProjectShareTargets(rulesetId: string, projectId: number): Observable<ProjectShareTarget[]> {
    return this.http.get<ProjectShareTarget[]>(`${this.projectSharesPath(rulesetId)}/targets`, {params: {projectId}});
  }

  /** Regelset in weitere Projekte teilen. Liefert alle Projekte, in denen es danach sichtbar ist. */
  shareWithProjects(rulesetId: string, projectId: number, projectIds: number[]): Observable<SharedProject[]> {
    return this.http.post<SharedProject[]>(this.projectSharesPath(rulesetId), {projectIds}, {params: {projectId}});
  }

  /**
   * Freigabe für ein Projekt aufheben. Das Backend entfernt dabei im gelösten Projekt alle Verweise
   * aus dessen Prüfläufen.
   */
  unshareFromProject(rulesetId: string, targetProjectId: number, projectId: number): Observable<SharedProject[]> {
    return this.http.delete<SharedProject[]>(`${this.projectSharesPath(rulesetId)}/${targetProjectId}`, {params: {projectId}});
  }

  /**
   * Macht ein Projekt, mit dem das Set geteilt ist, zum Eigentümer. `projectId` ist das bisherige
   * Eigentümerprojekt.
   *
   * @param keepCurrentOwner das bisherige Eigentümerprojekt behält das Set als geteiltes; sonst
   *                         verliert es das Set, und seine Prüfläufe werden bereinigt
   */
  transferOwnership(rulesetId: string, projectId: number, newOwnerProjectId: number,
                    keepCurrentOwner: boolean): Observable<SharedProject[]> {
    return this.http.post<SharedProject[]>(`${this.projectSharesPath(rulesetId)}/owner`,
      {newOwnerProjectId, keepCurrentOwner}, {params: {projectId}});
  }

  /** Map a CheckRuleSet to the generic Ruleset model */
  private mapCheckRuleSetToRuleset(s: CheckRuleSet): Ruleset {
    let setConfig: any = {};
    try {
      setConfig = s.configJson ? JSON.parse(s.configJson) : {};
    } catch {
      setConfig = {};
    }

    const setAllowedFileTypes = this.parseStoredAllowedFileTypes(setConfig.allowedFileTypes, ['PDF']);
    const setAllowedDocumentClasses = this.parseStoredDocumentClasses(setConfig.allowedDocumentClasses);
    const ruleAllowedFileTypeGroups = (s.rules ?? [])
      .map(rule => this.parseStoredAllowedFileTypes((rule as any)?.allowedFileTypes, []));
    const ruleAllowedDocumentClassGroups = (s.rules ?? [])
      .map(rule => this.parseStoredDocumentClasses((rule as any)?.allowedDocumentClasses));

    return {
      id: s.id ?? '',
      name: s.name ?? 'myCheck',
      description: s.description ?? '',
      type: 'KI',
      status: this.getKiRulesetStatus(s, setConfig),
      ruleCount: s.rules?.length ?? 0,
      allowedFileTypes: mergeRulesetAndRuleAllowedFileTypes(setAllowedFileTypes, ruleAllowedFileTypeGroups),
      allowedDocumentClasses: mergeRulesetAndRuleAllowedDocumentClasses(
        setAllowedDocumentClasses, ruleAllowedDocumentClassGroups),
      checkMode: this.parseStoredCheckMode(setConfig.checkMode),
      isPrebuilt: s.prebuilt ?? false,
      // Read-only bis das Backend-Flag ausgeliefert wird hilfsweise aus prebuilt abgeleitet,
      // damit bereitgestellte Sets sofort für normale Nutzer gesperrt sind.
      readOnly: (s as any).readOnly ?? s.prebuilt ?? false,
      pruefebene: typeof setConfig.pruefebene === 'string' ? setConfig.pruefebene : undefined,
      eingabeparameter: Array.isArray(setConfig.eingabeparameter) ? setConfig.eingabeparameter : undefined,
      identifikatorParamId: typeof setConfig.identifikatorParamId === 'string' ? setConfig.identifikatorParamId : undefined,
      createdAt: s.createdAt,
      updatedAt: s.updatedAt,
    };
  }

  private reportLoadFailure(message: string, err: unknown): void {
    console.error(`[RulesetService] ${message}`, err);
    if (this.recentLoadFailures.has(message)) return;
    this.recentLoadFailures.add(message);
    setTimeout(() => this.recentLoadFailures.delete(message), 5000);
    this.toastService.error(message);
  }

  private globalBaseFor(type: Ruleset['type']): string {
    if (type === 'KI') return this.kiGlobalBase;
    if (type === 'HYBRID') return this.hybridGlobalBase;
    return this.idsGlobalBase;
  }

  /**
   * Regel-IDs und -Namen eines HYBRID-Sets aus seinem Manifest – dieselbe Lesart wie
   * {@link getHybridRules} (ID = `nodeId` des Steps = ID der Kind-Definition).
   */
  private hybridRuleRefs(definition: WorkflowDefinition | null | undefined): { id?: string; name?: string }[] {
    if (!definition) return [];
    const manifest = this.flowService.parseManifest(definition);
    return (manifest.steps ?? []).map((step: any) => ({
      id: step.nodeId ?? undefined,
      name: step.inputs?.['__name'] ?? step.name ?? undefined,
    }));
  }

  /**
   * Ein KI-Set lesen.
   *
   * <p>Seit die /check-rules-Endpunkte projektgebunden sind, braucht der Aufruf eine `projectId`
   * (Fallback: aktuelles Projekt). Im Adminbereich gibt es kein Projekt – dort `adminGlobal = true`,
   * dann geht es über den globalen Endpunkt (nur mit Regelwerk-Admin-Recht).
   */
  getKiRuleset(id: string, projectId?: number, adminGlobal = false): Observable<CheckRuleSet> {
    if (adminGlobal) {
      return this.http.get<CheckRuleSet>(`${this.kiGlobalBase}/${id}`);
    }
    return this.http.get<CheckRuleSet>(`${this.basePath}/check-rules/${id}`, {
      params: {projectId: String(projectId ?? this.stateConfig.getProjectId())},
    });
  }

  private loadIdsRules(projectId?: number): Observable<Ruleset[]> {
    // Ohne Projekt lässt der AllowedPrivilege-Aspekt den Aufruf nicht durch (403) – wie bei KI
    // gibt es dann keine Projektliste.
    if (projectId == null) {
      return of([]);
    }
    const params: any = {category: 'IDS'};
    if (projectId != null) params.projectId = String(projectId);
    return this.http.get<any[]>(`${this.basePath}/workflows/rules`, {params}).pipe(
      // Projekt-eigene Regeln UND bereitgestellte globale (projectId == null). Das Backend liefert
      // globale nur, wenn PUBLISHED – Entwürfe sickern nicht durch. Globale → „Vorgefertigt".
      // Aus anderen Projekten geteilte Regeln tragen die projectId ihres Eigentümers und stehen
      // deshalb über sharedProjects auf der Liste – sonst fielen sie hier still heraus.
      map(rules => rules
        .filter(r => r.projectId === projectId || r.projectId == null
          || (r.sharedProjects ?? []).some((p: SharedProject) => p.projectId === projectId))
        .map(r => this.withProjectSharing(this.mapWorkflowCheckRuleToRuleset(r), r, projectId))),
      catchError(err => {
        this.reportLoadFailure('IDS-myChecks konnten nicht geladen werden.', err);
        return of([]);
      }),
    );
  }

  private mapGlobalIdsToRuleset(r: any): Ruleset {
    return {
      id: r.id ?? '',
      name: r.name ?? 'Globaler myCheck',
      description: r.description ?? '',
      type: 'IDS',
      status: (r.status as Ruleset['status']) ?? 'PUBLISHED',
      ruleCount: this.countIdsSpecifications(r.ruleData),
      allowedFileTypes: ['IFC'],
      allowedDocumentClasses: [],
      checkMode: 'SINGLE',
      isPrebuilt: true,
      readOnly: true,
      createdAt: r.createdAt,
      updatedAt: r.updatedAt,
    };
  }

  /** Update an existing KI ruleset */
  updateKiRuleset(id: string, ruleSet: CheckRuleSet, projectId?: number): Observable<CheckRuleSet> {
    return this.http.put<CheckRuleSet>(`${this.basePath}/check-rules/${id}`, ruleSet, {
      params: {projectId: String(projectId ?? this.stateConfig.getProjectId())},
    });
  }

  /** Delete a KI ruleset */
  deleteKiRuleset(id: string, projectId?: number, removeShares = false): Observable<any> {
    // Handgeschrieben statt checkRuleApi.delete1: der generierte Client kennt projectId und
    // removeShares erst nach der nächsten Neugenerierung.
    return this.http.delete(`${this.basePath}/check-rules/${id}`, {
      params: {
        projectId: String(projectId ?? this.stateConfig.getProjectId()),
        removeShares: String(removeShares),
      },
    });
  }

  private loadKiRulesets(projectId?: number): Observable<Ruleset[]> {
    // Ohne Projekt gibt es keine Projektliste mehr (früher: die Sets ALLER Projekte) – wie bei IDS
    // und HYBRID. Zentral bereitgestellte Sets lädt der Adminbereich über listGlobalRulesetsForSelection.
    if (projectId == null) {
      return of([]);
    }
    return this.http.get<CheckRuleSet[]>(`${this.basePath}/check-rules`, {params: {projectId: String(projectId)}}).pipe(
      map(sets => sets
        .filter(s => s.type !== 'IDS')
        // Globale (projektlose) Sets nur, wenn veröffentlicht – Entwürfe/Archivierte ausblenden.
        .filter(s => s.projectId != null || !(s as any).status || (s as any).status === 'PUBLISHED')
        .map(s => this.withProjectSharing(this.mapCheckRuleSetToRuleset(s), s, projectId))),
      catchError(err => {
        this.reportLoadFailure('KI-myChecks konnten nicht geladen werden.', err);
        return of([]);
      }),
    );
  }

  /** Add a rule to a KI ruleset */
  addKiRule(setId: string, rule: CheckRule, adminGlobal = false): Observable<CheckRule> {
    if (adminGlobal) {
      return this.http.post<CheckRule>(`${this.kiGlobalBase}/${setId}/rules`, rule);
    }
    return this.http.post<CheckRule>(`${this.basePath}/check-rules/${setId}/rules`, rule, {
      params: {projectId: String(this.stateConfig.getProjectId())},
    });
  }

  /** Update a single KI rule */
  updateKiRule(ruleId: string, rule: CheckRule, adminGlobal = false): Observable<CheckRule> {
    if (adminGlobal) {
      return this.http.put<CheckRule>(`${this.kiGlobalBase}/rules/${ruleId}`, rule);
    }
    return this.http.put<CheckRule>(`${this.basePath}/check-rules/rules/${ruleId}`, rule, {
      params: {projectId: String(this.stateConfig.getProjectId())},
    });
  }

  /** Delete a single KI rule */
  deleteKiRule(ruleId: string, adminGlobal = false): Observable<any> {
    if (adminGlobal) {
      return this.http.delete(`${this.kiGlobalBase}/rules/${ruleId}`);
    }
    return this.http.delete(`${this.basePath}/check-rules/rules/${ruleId}`, {
      params: {projectId: String(this.stateConfig.getProjectId())},
    });
  }

  private mapWorkflowCheckRuleToRuleset(r: any): Ruleset {
    return {
      id: r.id ?? '',
      name: r.name ?? 'IDS Prüfregel',
      description: r.description ?? '',
      type: 'IDS',
      status: this.getIdsRulesetStatus(r.ruleData),
      ruleCount: this.countIdsSpecifications(r.ruleData),
      allowedFileTypes: ['IFC'],
      allowedDocumentClasses: [],
      checkMode: 'SINGLE',
      isPrebuilt: r.projectId == null,
      readOnly: r.readOnly ?? (r.projectId == null),
      createdAt: r.createdAt,
      updatedAt: r.updatedAt,
    };
  }

  /** IDS rulesets are active only when the XML contains a complete IDS rule. */
  private getIdsRulesetStatus(ruleData: string | null | undefined): Ruleset['status'] {
    if (!ruleData?.trim()) return 'DRAFT';

    try {
      const rule = this.idsAdapter.fromXml(ruleData);
      return getIdsValidationIssues(rule).length === 0 ? 'ACTIVE' : 'DRAFT';
    } catch {
      return 'DRAFT';
    }
  }

  private countIdsSpecifications(ruleData: string | null | undefined): number {
    if (!ruleData) return 0;
    try {
      const doc = new DOMParser().parseFromString(ruleData, 'application/xml');
      const specsEl = Array.from(doc.documentElement.childNodes)
        .find((n: any) => n.localName === 'specifications') as Element | undefined;
      if (!specsEl) return 0;
      return Array.from(specsEl.childNodes)
        .filter((n: any) => n.localName === 'specification').length;
    } catch {
      return 0;
    }
  }

  // ─── Hybrid Rulesets (via Workflow Definitions API) ─────────────────────────

  /** Archive a hybrid ruleset (löscht nicht hart, siehe WorkflowService#archiveDefinition) */
  deleteHybridRuleset(id: string): Observable<any> {
    return this.flowService.archiveDefinition(id);
  }

  /** Create a new hybrid ruleset as a workflow definition */
  saveHybridRuleset(manifest: any, projectId: number): Observable<WorkflowDefinition> {
    return this.flowService.saveDefinition(manifest, projectId, 'HYBRID_RULESET');
  }

  private loadHybridRulesets(projectId?: number): Observable<Ruleset[]> {
    // Ohne Projekt 403 wie bei IDS – keine Projektliste.
    if (projectId == null) {
      return of([]);
    }
    return (this.definitionApi.listDefinitions(projectId, 'HYBRID_RULESET') as Observable<WorkflowDefinition[]>).pipe(
      map(defs => defs.map(d => this.withProjectSharing(this.mapDefinitionToRuleset(d), d, projectId))),
      catchError(err => {
        this.reportLoadFailure('Hybrid-myChecks konnten nicht geladen werden.', err);
        return of([]);
      }),
    );
  }

  /** Map a WorkflowDefinition to the generic Ruleset model */
  private mapDefinitionToRuleset(d: WorkflowDefinition): Ruleset {
    let ruleCount = 0;
    let setConfig: any = {};
    try {
      const manifest = JSON.parse(d.manifestJson ?? '{}');
      ruleCount = manifest.steps?.length ?? 0;
      setConfig = this.getHybridSetConfig(manifest);
    } catch { /* ignore */ }

    const setAllowedFileTypes = this.parseStoredAllowedFileTypes(setConfig.allowedFileTypes, ['IFC']);
    const setAllowedDocumentClasses = this.parseStoredDocumentClasses(setConfig.allowedDocumentClasses);
    const ruleAllowedFileTypeGroups = this.parseStoredRuleRestrictions(
      setConfig.ruleAllowedFileTypes,
      value => this.parseStoredAllowedFileTypes(value, []),
    );
    const ruleAllowedDocumentClassGroups = this.parseStoredRuleRestrictions(
      setConfig.ruleAllowedDocumentClasses,
      value => this.parseStoredDocumentClasses(value),
    );

    return {
      id: d.id ?? '',
      name: d.name ?? 'Hybrider myCheck',
      description: '',
      type: 'HYBRID',
      status: (d.status as Ruleset['status']) ?? 'DRAFT',
      ruleCount,
      allowedFileTypes: mergeRulesetAndRuleAllowedFileTypes(setAllowedFileTypes, ruleAllowedFileTypeGroups),
      allowedDocumentClasses: mergeRulesetAndRuleAllowedDocumentClasses(
        setAllowedDocumentClasses,
        ruleAllowedDocumentClassGroups,
      ),
      checkMode: this.parseStoredCheckMode(setConfig.checkMode),
      isPrebuilt: d.projectId == null,
      readOnly: (d as any).readOnly ?? (d.projectId == null),
      pruefebene: typeof setConfig.pruefebene === 'string' ? setConfig.pruefebene : undefined,
      eingabeparameter: Array.isArray(setConfig.eingabeparameter) ? setConfig.eingabeparameter : undefined,
      identifikatorParamId: typeof setConfig.identifikatorParamId === 'string' ? setConfig.identifikatorParamId : undefined,
      createdAt: d.createdAt,
      updatedAt: d.updatedAt,
    };
  }

  private getHybridSetConfig(manifest: any): any {
    const triggerConfig = manifest?.trigger?.config;
    if (triggerConfig?.hybridEditor && typeof triggerConfig.hybridEditor === 'object') {
      return triggerConfig.hybridEditor;
    }
    if (manifest?.config && typeof manifest.config === 'object') {
      return manifest.config;
    }
    return {};
  }

  /**
   * Prüfmodus aus der gespeicherten Set-Konfiguration. Bewusst auf die beiden bekannten Werte
   * normalisiert: der Modus steuert beim Direktstart die Ebenen-Reihenfolge des Berichts
   * (`defaultLevelOrderKeys`), ein unbekannter Wert aus einem Altbestands-`configJson` würde dort
   * still als „nicht COMBINED" durchschlagen – hier ist er wenigstens an einer Stelle festgelegt.
   */
  private parseStoredCheckMode(value: unknown): CheckMode {
    return value === 'COMBINED' ? 'COMBINED' : 'SINGLE';
  }

  private parseStoredDocumentClasses(value: unknown): string[] {
    if (!Array.isArray(value)) return [];
    return value.filter((v): v is string => typeof v === 'string').map(v => v.trim()).filter(Boolean);
  }

  private parseStoredAllowedFileTypes(value: unknown, fallback: string[]): string[] {
    const values = Array.isArray(value)
      ? value
      : typeof value === 'string'
        ? value.split(',').map(v => v.trim()).filter(Boolean)
        : [];

    const normalized = values
      .map(v => String(v).toUpperCase())
      .map(v => {
        if (v === '.IFC' || v === 'IFC') return 'IFC';
        if (v === '.PDF' || v === 'PDF') return 'PDF';
        if (v === '.XLSX' || v === '.XLS' || v === 'EXCEL' || v === 'XLSX') return 'EXCEL';
        if (v === '.DOCX' || v === '.DOC' || v === 'WORD') return 'WORD';
        if (v === '.PPTX' || v === '.PPT' || v === 'POWERPOINT') return 'POWERPOINT';
        return '';
      })
      .filter(Boolean);

    return normalized.length > 0 ? normalized : fallback;
  }

  private parseStoredRuleRestrictions(
    value: unknown,
    parseRestrictionValues: (entry: unknown) => string[],
  ): string[][] {
    if (!value || typeof value !== 'object' || Array.isArray(value)) return [];
    return Object
      .values(value as Record<string, unknown>)
      .map(entry => parseRestrictionValues(entry))
      .filter(group => group.length > 0);
  }

  private fileNameFromResponse(contentDisposition: string | null): string {
    const fallback = `pruefregelset.${RULESET_PACKAGE_EXTENSION}`;
    if (!contentDisposition) return fallback;
    // filename*=UTF-8''… hat Vorrang vor dem einfachen filename="…" (RFC 5987).
    const encoded = /filename\*=UTF-8''([^;]+)/i.exec(contentDisposition);
    if (encoded) {
      try {
        return decodeURIComponent(encoded[1].trim());
      } catch {
        // Ungültige Prozent-Kodierung: auf den einfachen Namen zurückfallen.
      }
    }

    const plain = /filename="?([^";]+)"?/i.exec(contentDisposition);
    return plain ? plain[1].trim() : fallback;
  }

  // ─── Einzelne Regeln eines Regelsets (typunabhängig) ───────────────────────

  /**
   * KI-Regelsets gelten als "ACTIVE", wenn alle als Pflicht markierten Prüfparameter
   * in jeder Regel belegt sind. Andernfalls werden sie als "DRAFT" markiert.
   */
  private getKiRulesetStatus(checkRuleSet: CheckRuleSet, setConfig: any): Ruleset['status'] {
    const rules = Array.isArray(checkRuleSet.rules) ? checkRuleSet.rules : [];
    if (rules.length === 0) {
      return 'DRAFT';
    }

    const checkParameters = Array.isArray(setConfig?.checkParameters) ? setConfig.checkParameters : [];
    const requiredParameterIds: string[] = checkParameters
      .filter((param: any) => param?.necessity === 'Pflicht' && typeof param?.id === 'string')
      .map((param: any) => param.id as string);

    if (requiredParameterIds.length === 0) {
      return 'ACTIVE';
    }

    const hasMissingRequiredValue = rules.some((rule: any) => {
      let ruleConfig: any = {};
      try {
        ruleConfig = rule?.configJson ? JSON.parse(rule.configJson) : {};
      } catch {
        ruleConfig = {};
      }
      const parameterValues = ruleConfig?.parameterValues ?? {};
      return requiredParameterIds.some((paramId: string) => !String(parameterValues[paramId] ?? '').trim());
    });

    return hasMissingRequiredValue ? 'DRAFT' : 'ACTIVE';
  }

  /**
   * Lädt die rohe IDS-XML eines Regelsets. Wird gebraucht, um bei einer manuellen Teilauswahl
   * einzelner Spezifikationen innerhalb des Sets clientseitig eine reduzierte XML zu bauen
   * (siehe IdsAdapterService.filterSpecificationsByName).
   */
  getIdsRulesetXml(rulesetId: string, projectId?: number): Observable<string> {
    const params: any = {};
    if (projectId != null) params.projectId = String(projectId);
    return this.http.get<any>(`${this.basePath}/workflows/rules/${rulesetId}`, {params}).pipe(
      map(rule => rule.ruleData ?? ''),
      catchError(() => of('')),
    );
  }

  /**
   * Individuelle Farbschwellen eines IDS-Prüfregelsets (#2309), oder `null`, wenn keine
   * konfiguriert sind — dann färbt der Bericht wie bisher.
   *
   * Best-effort wie {@link getIdsRulesetXml}: ein Fehler darf den Bericht nicht kosten.
   */
  getIdsRulesetThresholds(rulesetId: string, projectId?: number): Observable<IdsThresholds | null> {
    const params: any = {};
    if (projectId != null) params.projectId = String(projectId);
    return this.http.get<any>(`${this.basePath}/workflows/rules/${rulesetId}`, {params}).pipe(
      map(rule => parseIdsThresholds(rule?.configJson)),
      catchError(() => of(null)),
    );
  }

  private parseIdsSpecsAsRules(rulesetId: string, ruleData: string | null): RulesetRule[] {
    if (!ruleData) return [];
    try {
      const doc = new DOMParser().parseFromString(ruleData, 'application/xml');
      const specsEl = Array.from(doc.documentElement.childNodes)
        .find((n: any) => n.localName === 'specifications') as Element | undefined;
      if (!specsEl) return [];
      return Array.from(specsEl.childNodes)
        .filter((n: any) => n.localName === 'specification')
        .map((el: any) => ({
          id: `${rulesetId}::${(el as Element).getAttribute('name') ?? ''}`,
          name: (el as Element).getAttribute('name') ?? 'Unbenannte Spezifikation',
          description: (el as Element).getAttribute('description') ?? undefined,
          code: undefined,
        }));
    } catch {
      return [];
    }
  }

  /** Get hybrid rules (workflow definition steps) as HybridRule[] */
  getHybridRules(definitionId: string): Observable<HybridRule[]> {
    return this.flowService.getDefinition(definitionId).pipe(
      map(def => {
        const manifest = this.flowService.parseManifest(def);
        return (manifest.steps ?? []).map((step: any) => ({
          id: step.nodeId ?? '',
          // Der Anzeigename der Hybrid-Regel wird beim Speichern im Hybrid-Editor in
          // step.inputs['__name'] abgelegt (und synchron gehalten). Fallbacks: step.name,
          // moduleId (z. B. 'HYBRID_RULE') oder generisch 'Prüfregel'.
          name: step.inputs?.['__name'] ?? step.name ?? step.moduleId ?? 'Prüfregel',
          description: step.inputs?.['__description'] ?? '',
          // Das Kürzel wird im Hybrid-Editor in step.inputs['__shortCode'] abgelegt (siehe hybrid-editor.component.ts).
          shortCode: step.inputs?.['__shortCode'] ?? '',
          moduleCount: 1,
        }));
      }),
      catchError(() => of([])),
    );
  }
}
